import { useEffect, useRef, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box"
import Backdrop from "@mui/material/Backdrop"
import CircularProgress from "@mui/material/CircularProgress"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";
import { green, grey, lightBlue, red } from "@mui/material/colors";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual, isUndefined, size, replace, values } from "lodash";
import { isNull } from "lodash/lang";

import runRpService from "@plannel/services/rp/run-rp-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";

import PaginationContainer from "@plannel/components/PaginationContainer"
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import CycleCalendarRenderer from "@plannel/components/aggrid/CycleCalendarRenderer";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader"
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import GridDatePicker from "@plannel/components/GridDatePicker";
import { BulkValueUpdateButton, CreateButton, DeleteButton, ResultButton, SaveButton, RunButton, SettingsButton, FilterButton } from "@plannel/components/ActionIconButton";
import RpBulkValueUpdateModal from "../rp-modal/RpBulkValueUpdateModal";
import CreateVersionModal from "@plannel/components/modal/CreateVersionModal";
import RunModal from "@plannel/components/modal/RunModal";
import VersionSettingsModal from "@plannel/components/modal/VersionSettingsModal";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import RpUtil from "../utils/rp-util";
import dateUtils, { DEFAULT_DATE_FORMAT } from "@plannel/utils/date-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { reloadRpVersionFilter } from "@plannel/components/filter/RpVersionFilter";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { rpPolicyCd, versionStatusCode, dayOfWeek, buckets, module, limitVersion } = CodeUtil;
const { P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM } = rpPolicyCd;
const { PLANNED, UNPLANNED, SCENARIO, SIMULATION, RELEASED } = versionStatusCode;
const { RP, DP } = module;
const RP_POLICY_LIST = [P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM];
const { DAY, WEEK, MONTH } = buckets;
const INV_CLASS_CD_LIST = CodeUtil.dpABCConfig;

const { configDefaultValue } = RpUtil;
const { snackBarMessageColor } = ColorUtil;
const { itemType } = CodeUtil;
const RunRp = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const startDay = dayOfWeek.MONDAY.cd;

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [planVersions, setPlanVersions] = useState([]);
  const [dpVersions, setDpVersions] = useState([]);
  const [rpPolicies, setRpPolicies] = useState([]);
  const [selectItemHrchy, setSelectItemHrchy] = useState([]);
  const [useShipmentTxn, setUseShipmentTxn] = useState(false);
  const [useMpProductionPlan, setUseMpProductionPlan] = useState(false);
  const periodForAvgSales = useRef(configDefaultValue.salesRefStart);
  // configDefaultValue 를 그대로 담으면 그 안의 start(Date)가 모듈 전역 인스턴스라, 아래 월초/주초 보정이 상수를 직접 바꿔 다른 화면까지 새어 나간다. 항상 복사본을 들고 있는다.
  const rpSettingValue = useRef({ ...configDefaultValue, start: new ZDate(configDefaultValue.start) });
  const [isRun, setIsRun] = useState(false);
  const [version, setVersion] = useState(reduxViewState?.version ?? null);
  // const [locationFilter, setLocationFilter] = useState(reduxViewState?.location ? reduxViewState.location : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [defaultValue, setDefaultValue] = useState([]);

  const [onlyRpApplied, setOnlyRpApplied] = useState(reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "onlyRpApplied") ? reduxViewState.onlyRpApplied : true);
  const [sortParams, setSortParams] = useState({});

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);

  const [createNewModalOpen, setCreateNewModalOpen] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [rpConfigModalOpen, setRpConfigModalOpen] = useState(false);
  const [massUpdateModalOpen, setMassUpdateModalOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");
  const [openFilter, setOpenFilter] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});
  const EXCEL_EXPORT = 'excelExport'

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addedBulkUpdateTargets = [{ headerName: "baseDt", field: "baseDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day", popupGrid: true }, width: 130, valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd") },
    { headerName: "itemType", field: "itemType", width: 130, cellClass: 'stringType', cellEditor: "agRichSelectCellEditor", type: ["defaultValueParser"], cellEditorPopup: true,
      cellEditorParams: {
        values: [{ cd: "all", name: "all" }, ...Object.values(itemType).filter(itemType => !["RM", "SFG"].includes(itemType.cd))],
        valueFormatter: params => {
          if (!params.value) return t("all");
          return params.value.name ? t(params.value.name) : t("all");
        }
      },
      valueFormatter: params => {
        if (!params.value) return t("all");
        return params.value.name ? t(params.value.name) : t("all");
      }
    },
  ];
  const updateTargets = ["cycleInvFlg", "rpApplied", "orderMethod", "orderCalendar", "demandCalendar", "opInvDays", "moq", "ioq"];

  const currColsCollapsed = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "currColsCollapsed") ? reduxViewState.currColsCollapsed : true;
  const planColsCollapsed = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "planColsCollapsed") ? reduxViewState.planColsCollapsed : false;

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (version?.id) {
      searchFilters.versionId = version.id;
    }

    if (filterValue?.locations) {
      searchFilters.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (filterValue?.category) {
      searchFilters.hrchyCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    searchFilters.onlyRpApplied = onlyRpApplied;

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }
    params.searchFilters = searchFilters;

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
          params.advancedFilters = advancedFilters;
    }

    return { ...params, ...sortParams };
  };

  const objCellRenderer = params => {
    const { colId } = params.column.getColDef();

    if (["locName", "itemName", "categoryName", "originLocation", "planVersionName"].includes(colId)) {
      return params?.value ? params.value.name : "";
    }

    if (colId === "locCd") {
      return params.value?.locCd;
    }

    if (colId === "categoryCd") {
      return params.value?.hrchyCd;
    }

    return !isNull(params.value) ? params.value[colId] : "";
  };

  const cellStyle = (params) => {
    const { column, data } = params;

    if (!data.rpFlg) {
      return { background: grey[300] };
    }

    const editableColumns = ["cycleInvFlg", "rpFlg", "rpPolicyCd", "moq", "ioq", "demandCalendar", "otdLtDays", "targetSupplyDays"];
    if (editableColumns.includes(column.colId)) {
      if (isEqual(column.colId, "moq")) {
        if (!data.rpPolicyCd) {
          return { color : grey[400] };
        }
      }

      if (isEqual(column.colId, "ioq")) {
        if (!data.rpPolicyCd) {
          return { color : grey[400] };
        }
      }

      if (isEqual(column.colId, "rpPolicyCd")) {
        if (!data.rpPolicyCd) {
          return { color : grey[400] };
        }
      }

      return { background: lightBlue[50] };
    }

    const styles = {};
    if (isEqual(column.colId, "opInvDays")) {
      styles.background = lightBlue[50];
    } else if (isEqual(column.colId, "rop")) {
      if (isEqual(data.rpPolicyCd, MIN_MAX_SYSTEM) || isEqual(data.rpPolicyCd, Q_SYSTEM)) {
        styles.background = lightBlue[50];
      }
    }

    if (isEqual(column.colId, "replenishCalendar")) {
      if (isEqual(data.rpPolicyCd, P_SYSTEM) || isEqual(data.rpPolicyCd, MIN_MAX_SYSTEM)) {
        styles.background = lightBlue[50];
      } else {
        styles.color = grey[400];
      }
    }

    return styles;
  }

  const dynamicCellEditable = params => {
    const { column, data } = params;

    if (!data.rpFlg) {
      return false;
    }

    return isEqual(column.colId, "replenishCalendar") && (isEqual(data.rpPolicyCd, P_SYSTEM) || isEqual(data.rpPolicyCd, MIN_MAX_SYSTEM));
  }

  const decimalFormatter = (params, decimal = 0) => {
    if (!isNull(params.value)) {
      return NumberUtil.formatNumberData(params,  { maximumFractionDigits: decimal, minimumFractionDigits: decimal })
    }

    return params.value;
  }

  const objectComparator = (value1, value2, colId) => {
    const property = ["itemCd", "locCd"].includes(colId) ? colId : "name";
    const val1 = !value1 ? "" : (value1[property] ?? "");
    const val2 = !value2 ? "" : (value2[property] ?? "");
    if (val1 === val2) {
      return 0;
    } else if (val1 > val2) {
      return 1;
    }
    return -1;
  }

  const dynamicEditable = params => {
    const { rpPolicyCd } = params.data;

    return rpPolicyCd ? true : false;
  };

  const currencyRenderer = (param, isExport = false) => {
    let locCd = param?.data?.locCd;
    if (param?.data?.location) {
      locCd = param?.data?.location?.locCd;
    }
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }

    const value = param.data?.[param?.colDef?.field] ?? 0;
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (!isExport) {
      if (value < 0) {
        return `-${symbol}${replace(formatValue, "-", "")}`;
        }
     return symbol + formatValue;
    } else {
      return formatValue.replace(/,/g, "");
    }
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const itemTypeValueFormatter = params => {
    const { value } = params;
    return itemType?.[value] ? GridUtils.gridValueL10N(itemType[value]?.name) : value;
  };

  const columnDefs = [
    {
      headerName: "locCd", colId: "locCd", field: "location",
      type: ["nonEditableColumn", "defaultValueFormatter"],
      valueFormatter: objCellRenderer, width: 100, pinned: "left", lockPinned: true, hide: true,
      comparator: (value1, value2) => objectComparator(value1, value2, "locCd")
    },
    {
      headerName: "locName", colId: "locName", field: "location",
      type: ["nonEditableColumn", "defaultValueFormatter"],
      valueFormatter: objCellRenderer, width: 130, pinned: "left", lockPinned: true,
      comparator: (value1, value2) => objectComparator(value1, value2, "locName"), filterType: "string"
    },
    {
      headerName: "itemCd", colId: "itemCd", field: "item",
      type: ["nonEditableColumn", "defaultValueFormatter"],
      valueFormatter: objCellRenderer, width: 130, pinned: "left", lockPinned: true, cellClass: 'stringType',
      comparator: (value1, value2) => objectComparator(value1, value2, "itemCd"), filterType: "string"
    },
    {
      headerName: "itemName", colId: "itemName", field: "item",
      type: ["nonEditableColumn", "defaultValueFormatter"],
      valueFormatter: objCellRenderer, width: 200, pinned: "left", lockPinned: true,
      comparator: (value1, value2) => objectComparator(value1, value2, "itemName"), filterType: "string"
    },
    {
      headerName: "itemType", colId: "itemType", field: "itemType",
      type: ["nonEditableColumn", "defaultValueFormatter"],
      width: 200, pinned: "left", lockPinned: true, valueFormatter: itemTypeValueFormatter,
      filterType: "string", filterOption: Object.keys(itemType).filter(key => !["RM", "SFG"].includes(key)).map(key => key.toLowerCase())
    },
    {
      headerName: "category", colId: "categoryName", field: "itemHrchy",
      type: ["nonEditableColumn", "defaultValueFormatter"],
      valueFormatter: objCellRenderer, width: 130,
      comparator: (value1, value2) => objectComparator(value1, value2, "categoryName"), filterType: "string"
    },
    { headerName: "baseDt", colId: "invDate", field: "invDate", type: ["nonEditableColumn"], width: 100, filterType: "date" },
    { headerName: "stockQty", colId: "invQty", field: "invQty", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 100, filterType: "number" },
    { headerName: "stockAmt", colId: "invAmt", field: "invAmt", type: ["numberColumn", "rightAligned", "nonEditableColumn"], valueFormatter: currencyRenderer, width: 105, filterType: "number" },
    { headerName: "rpApplied", colId: "rpFlg", field: "rpFlg", type: ["booleanColumn"], width: 100, cellStyle, filterType: "boolean" },
    { headerName: "invClassCd", field: "invClassCd", type: ["nonEditableColumn"], width: 120, cellStyle, cellRenderer: AbcXyzChip, filterType: "string", filterOption: INV_CLASS_CD_LIST },
    {
      headerName: "orderMethod", colId: "rpPolicyCd", field: "rpPolicyCd",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: params => GridUtils.gridValueL10N(params.value),
      cellEditorParams: {
        values: RP_POLICY_LIST
      },
      width: 130, cellStyle, editable: dynamicEditable,
      filterType: "string", filterOption: RP_POLICY_LIST
    },
    {
      headerName: "targetInvQty", colId: "targetInvQty", field: "targetInvQty",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 140, cellStyle,
      valueFormatter: decimalFormatter,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: currColsCollapsed, reduxKey: "currColsCollapsed",
        detailColumns: [
          "targetInvAmt",
          "safetyInvQty",
          "cycleInvQty",
          "cycleInvFlg",
          "opInvQty",
          "rop",
          "dailySalesQtyAvg",
          "opInvDays",
          "targetSupplyDays",
          "otdLtDays",
          "originLocation",
          "replenishCalendar",
          //"demandCalendar",
          "moq",
          "ioq"
        ]
      }, filterType: "number"
    },
    {
      headerName: "targetInvAmt", colId: "targetInvAmt", field: "targetInvAmt",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"], valueFormatter: currencyRenderer,
      width: 120, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01", filterType: "number"
    },
    {
      headerName: "cycleStockApplied", colId: "cycleInvFlg", field: "cycleInvFlg", type: ["booleanColumn"], width: 140, cellStyle,
      hide: currColsCollapsed, headerClass: "header-color-01", filterType: "boolean"
    },
    {
      headerName: "cycleInvQty", colId: "cycleInvQty", field: "cycleInvQty",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 120, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01", filterType: "number",
      valueFormatter: decimalFormatter
    },
    {
      headerName: "safetyStockQty", colId: "safetyInvQty", field: "safetyInvQty",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 120, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01",
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "opInvQty", colId: "opInvQty", field: "opInvQty",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 120, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01",
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "rop", colId: "rop", field: "rop",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 100, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01",
      valueFormatter: decimalFormatter,
      editable: params => {
        const { data } = params;
        return isEqual(data?.rpPolicyCd, MIN_MAX_SYSTEM) || isEqual(data?.rpPolicyCd, Q_SYSTEM);
      }, filterType: "number"
    },
    {
      headerName: "avgSalesQtyPerDay", colId: "dailySalesQtyAvg", field: "dailySalesQtyAvg",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 120, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01", filterType: "number",
      valueFormatter: params => decimalFormatter(params, 2)
    },
    {
      headerName: "opInvDays", colId: "opInvDays", field: "opInvDays",
      type: ["numberColumn", "rightAligned"],
      width: 120, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01", filterType: "number"
    },
    {
      headerName: "targetSupplyDays", colId: "targetSupplyDays", field: "targetSupplyDays",
      type: ["numberColumn", "rightAligned"],
      width: 120, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01", filterType: "number"
    },
    {
      headerName: "otdLtD", colId: "otdLtDays", field: "otdLtDays",
      type: ["numberColumn", "rightAligned"],
      width: 110, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01", filterType: "number"
    },
    {
      headerName: "suppliedBy", colId: "originLocation", field: "originLocation", type: ["nonEditableColumn", "defaultValueFormatter"],
      valueFormatter: objCellRenderer, width: 110, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01",
      comparator: (value1, value2) => objectComparator(value1, value2, "originLocation"), filterType: "string"
    },
    {
      headerName: "orderCalendar", colId: "replenishCalendar", field: "replenishCalendar", type: "nonEditableColumn",
      width: 160, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01",
      comparator: (value1, value2) => objectComparator(value1, value2, "replenishCalendar"),
      valueFormatter: params => {
        const { value } = params;
        return value?.name ? value.name : t("notConfigured");
      },
      cellRenderer: CycleCalendarRenderer, cellRendererParams: { setSnackInfo, editable: dynamicCellEditable }, filterType: "string"
    },
    // {
    //   headerName: "demandCalendar", colId: "demandCalendar", field: "demandCalendar", type: "nonEditableColumn", sortable: false,
    //   width: 160, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01",
    //   valueFormatter: params => {
    //     const { value } = params;
    //     return value?.name ? value.name : t("notConfigured");
    //   },
    //   cellRenderer: CycleCalendarRenderer, cellRendererParams: { setSnackInfo }
    // },
    {
      headerName: "moq", colId: "moq", field: "moq",
      type: ["numberColumn", "rightAligned"],
      width: 100, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01", editable: dynamicEditable,
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "ioq", colId: "ioq", field: "ioq",
      type: ["numberColumn", "rightAligned"],
      width: 100, cellStyle, hide: currColsCollapsed, headerClass: "header-color-01", editable: dynamicEditable,
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "openPoQ", colId: "openPoQty", field: "openPoQty",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 100, cellStyle,
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "newRpQ", colId: "newRpQty", field: "newRpQty",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 140, cellStyle,
      valueFormatter: decimalFormatter,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: planColsCollapsed, reduxKey: "planColsCollapsed",
        detailColumns: [
          "rpFreq",
          "rpOrderQtyAvg",
          "salesQty",
          "eohQtyAvg",
          "eohAmtAvg",
          "eohaeQty",
          "eohaeAmt"
        ]
      }, filterType: "number"
    },
    {
      headerName: "rpFreq", colId: "rpFreq", field: "rpFreq",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 110, hide: planColsCollapsed, cellStyle, headerClass: "header-color-01",
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "rpOrderQtyAvg", colId: "rpOrderQtyAvg", field: "rpOrderQtyAvg",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 120, hide: planColsCollapsed, cellStyle, headerClass: "header-color-01",
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "shipmentQty", colId: "salesQty", field: "salesQty",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 110, hide: planColsCollapsed, cellStyle, headerClass: "header-color-01",
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "eohQtyAvg", colId: "eohQtyAvg", field: "eohQtyAvg",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 120, hide: planColsCollapsed, cellStyle, headerClass: "header-color-01",
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "eohAmtAvg", colId: "eohAmtAvg", field: "eohAmtAvg",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"], valueFormatter: currencyRenderer,
      width: 120, hide: planColsCollapsed, cellStyle, headerClass: "header-color-01", filterType: "number"
    },
    {
      headerName: "eohaeQty", colId: "eohaeQty", field: "eohaeQty",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      width: 130, hide: planColsCollapsed, cellStyle, headerClass: "header-color-01",
      valueFormatter: decimalFormatter, filterType: "number"
    },
    {
      headerName: "eohaeAmt", colId: "eohaeAmt", field: "eohaeAmt",
      type: ["numberColumn", "rightAligned", "nonEditableColumn"], valueFormatter: currencyRenderer,
      width: 130, hide: planColsCollapsed, cellStyle, headerClass: "header-color-01", filterType: "number"
    }
  ];

  const getRepeatCount = (calendar, target) => {
    const { dowMonFlg, dowTueFlg, dowWedFlg, dowThuFlg, dowFriFlg, dowSatFlg, dowSunFlg, wom1wFlg, wom2wFlg, wom3wFlg, wom4wFlg, wom5wFlg } = calendar;

    if (isEqual(target, "dow")) {
      const dowList = [dowMonFlg, dowTueFlg, dowWedFlg, dowThuFlg, dowFriFlg, dowSatFlg, dowSunFlg].filter(dow => isEqual(dow, true));
      return dowList ? dowList.length : 0;
    } else if (isEqual(target, "wom")) {
      const womList = [wom1wFlg, wom2wFlg, wom3wFlg, wom4wFlg, wom5wFlg].filter(wom => isEqual(wom, true));
      return womList ? womList.length : 0;
    }

    return 0;
  }

  const onCellValueChanged = params => {
    const gridApi = gridRef.current.api;
    if (gridApi) {
      const { column, node, data, value, newValue, oldValue } = params;
      const colId = column.getColId();

      if (isEqual(colId, "rpPolicyCd")) {
        gridApi.refreshCells({
          force: true,
          columns: ["targetInvAmt", "safetyInvQty", "cycleInvQty", "opInvQty", "rop", "opInvDays", "replenishCalendar"],
          rowNodes: [node]
        });
      } else if (isEqual(colId, "rpFlg")) {
        gridApi.refreshCells({
          force: true,
          columns: [
            "invClassCd", "rpPolicyCd", "targetInvQty", "targetInvAmt", "safetyInvQty",
            "cycleInvQty", "opInvQty", "rop", "dailySalesQtyAvg", "opInvDays",
            "otdLtDays", "originLocation", "replenishCalendar", "demandCalendar", "moq", "ioq",
            "openPoQty", "newRpQty", "rpFreq", "rpOrderQtyAvg",
            "salesQty", "eohQtyAvg", "eohAmtAvg", "eohaeQty", "eohaeAmt"
          ],
          rowNodes: [node]
        });
      } else if (["moq", "ioq"].includes(colId)) {
        if (value && isNaN(newValue)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotInNumericFormat") });
          node.setDataValue(column, oldValue);
        }

        if (!value || value === "") {
          node.setDataValue(column, null);
        }
      } else if (isEqual(colId, "replenishCalendar")) {
        const otdLtDays = Math.ceil(data.otdLtDays);
        let days = 0;
        if (isNull(newValue)) {
          const bucket = version?.planConfigObj?.BUCKET_UNIT ?? DAY;

          switch (bucket) {
            case WEEK:
              days = 7;
              break;
            case MONTH:
              days = 30;
              break;
            default:
              days = 0;
              break;
          }
        } else {
          const { cycleCd, intervalVal, domObj } = newValue;
          const dowCount = getRepeatCount(newValue, "dow");
          const womCount = getRepeatCount(newValue, "wom");

          switch (cycleCd) {
            case "D":
              days = intervalVal - 1;
              break;
            case "W":
              days = 7 * intervalVal / dowCount;
              break;
            case "M":
              if (domObj && domObj?.onDates) {
                days = 30 * intervalVal / domObj.onDates.length;
              } else {
                days = 30 * intervalVal / (dowCount * womCount);
              }
              break;
            default:
              days = 30 * 12 * intervalVal;
              break;
          }
        }

        if (!isEqual(data.opInvDays, days)) {
          const opInvDays = days - otdLtDays > 0 ? days - otdLtDays : 0;
          node.setDataValue("opInvDays", opInvDays);
          setTimeout(() => { gridApi.flashCells({ rowNodes: [node], columns: ["opInvDays"], fadeDelay: 1000 }); }, 300);
        }
      }
    }

    defaultGridMemo.onCellValueChanged(params);
  }

  const retrieveReplenishmentPlan = () => {
    gridRef.current?.api?.setGridOption('loading', true);

    const params = getRequestParams();
    runRpService.getRpPolicies(params)
      .then(response => {
        const { results, totalPages, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setRpPolicies(results);
        setGridPages(totalPages);

        if (size(currency)) {
          currencyMap.current = currency;
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, onlyRpApplied, version, currentPage, pageSize, advancedFilters });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);

        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));
        DataState.initialize(gridRef.current?.api);
        setProgressOn(false);
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const sortDpVersions = versions => {
    const releaseVersions = [];
    const scenarioVersions = [];

    for (const ver of versions) {
      const { statusCd } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        scenarioVersions.push(ver);
      }
    }

    return [...releaseVersions, ...scenarioVersions];
  }

  const sortRpVersions = versions => {
    const basicVersions = [];
    const executedVersions = [];
    const releaseVersions = [];

    for (const ver of versions) {
      const { statusCd, execTs } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        if (isNull(execTs)) {
          basicVersions.push(ver);
        } else {
          executedVersions.push(ver);
        }
      }
    }
    const list = [...basicVersions, ...executedVersions, ...releaseVersions];
    if (size(list) > limitVersion) {
      return list.slice(0, limitVersion);
    }
    return list;
  }

  const retrieveDpVersion = () => {
    planVersionService.getAll({ moduleCd: DP, statusCds: [RELEASED, SCENARIO], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        setDpVersions(sortDpVersions(results));
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveRpVersion = (receivedVersion) => {
    planVersionService.getAll({ moduleCd: RP, statusCds: [UNPLANNED, PLANNED, SIMULATION, RELEASED], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          const sortedVersions = sortRpVersions(results);
          setPlanVersions(sortedVersions);

          if (receivedVersion) {
            const currentVersion = results.find(result => isEqual(result.id, receivedVersion.id));
            currentVersion ? setVersion(currentVersion) : setVersion(results[0]);
          } else {
            if (reduxViewState?.version) {
              const reduxVersion = results.find(result => isEqual(result.id, reduxViewState.version.id));
              if (!isUndefined(reduxVersion)) {
                setVersion(reduxVersion);
              } else {
                setVersion(sortedVersions[0]);
              }
            } else {
              setVersion(sortedVersions[0]);
            }
          }
        } else {
          setPlanVersions([]);
          setVersion(null);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const getWeekStartDate = (curDate) => {
    const weekDay = values(dayOfWeek).filter(week => isEqual(week.cd, startDay))[0];
    const day = curDate.getDay();
    const computeDays = Number(weekDay.id);

    if (curDate.getDay() !== 1) {
      const convertDate = Number(curDate.getDate() - day + computeDays);
      const monthAllDays = new ZDate(curDate.getFullYear(), curDate.getMonth() + 1, 0).getDate();
      if (convertDate > monthAllDays) {
        return new ZDate(curDate.getFullYear(), curDate.getMonth() + 1, convertDate - monthAllDays);
      }
      return new ZDate(curDate.setDate(convertDate));
    }

    return curDate;
  };

  const retrievePeriodForAvgSales = () => {
    return planConfigService.getAll({ moduleCd: CodeUtil.module.RP })
      .then(response => {
        const { results } = response.data;

        // SALES_REF_PERIOD 를 먼저 확정한다 — RP_SETTINGS_CONFIG 의 salesRefStart 폴백으로 쓰이므로
        // 응답에서 어느 쪽이 먼저 오는지에 기대면 안 된다(예전엔 순서에 따라 폴백이 빗나갔다).
        // findLast: 같은 configCd 가 여러 건 와도 예전 for-loop 과 같은 "마지막 행이 이긴다"를 유지한다.
        const salesRefPeriod = results?.findLast(item => item.configCd === "SALES_REF_PERIOD");
        periodForAvgSales.current = salesRefPeriod?.numVal ? Number(salesRefPeriod.numVal) : configDefaultValue.salesRefStart;

        const objVal = results?.findLast(item => item.configCd === "RP_SETTINGS_CONFIG")?.objVal;
        // start 는 Date — 얕은 복사로는 configDefaultValue 와 같은 인스턴스를 공유하게 되고,
        // 아래 월초/주초 보정이 모듈 전역 상수를 직접 변형해 다른 화면까지 새어 나간다.
        const data = { ...configDefaultValue, start: new ZDate(configDefaultValue.start) };

        data.bucket = objVal?.BUCKET_UNIT ?? data.bucket;
        data.range = objVal?.PLANNING_RANGE ?? data.range;
        data.demandDistributionPolicy = objVal?.DEMAND_DISTRIBUTION_POLICY ?? data.demandDistributionPolicy;
        data.salesRefStart = objVal?.SALES_REF_START ?? periodForAvgSales.current;
        data.salesRefEnd = objVal?.SALES_REF_END ?? data.salesRefEnd;
        data.demandRefStartDate = objVal?.DEMAND_REF_START ?? data.demandRefStartDate;
        data.demandRefEndDate = objVal?.DEMAND_REF_END ?? data.demandRefEndDate;
        data.backorder = objVal?.BACKORDER ?? data.backorder;
        data.immediateShipment = objVal?.IMMEDIATE_SHIPMENT ?? data.immediateShipment;
        data.noteFlg = objVal?.NOTE_FLG ?? data.noteFlg;
        data.multiSourcingFlg = objVal?.MULTI_SOURCING_FLG ?? data.multiSourcingFlg;
        data.kitDemandMergeFlg = objVal?.KIT_DEMAND_MERGE_FLG ?? data.kitDemandMergeFlg;
        data.useTargetSupplyDays = objVal?.USE_TARGET_SUPPLY_DAYS ?? data.useTargetSupplyDays;

        if (data.bucket === MONTH) {
          data.start.setDate(1);
        } else if (data.bucket === WEEK) {
          data.start = getWeekStartDate(data.start);
        }

        // RP_SETTINGS_CONFIG 가 없어도 채운다 — Create/Version Settings 두 모달이 모두 이 값을 기본값으로 쓴다.
        rpSettingValue.current = data;
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveItemHrchies = () => {
    itemHrchyService.getAll({ searchFilters: { hrchyNum: CodeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const { results } = response.data;
        if (results !== null) {
          setDefaultValue(results);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const removeVersion = () => {
    gridRef.current.api.stopEditing(false);

    setProgressOn(true);
    if (!isEmpty(version)) {
      runRpService.removeVersion(version)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });

          setRpPolicies([]);
          retrieveRpVersion();
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          setProgressOn(false);
        });
    }
  }

  const hasDpVersion = () => {
    const dpVersionId = version?.planConfigObj?.DP_VERSION_ID;

    if (dpVersionId) {
      const dpVersionIds = dpVersions.map(ver => ver.id);
      if (!isEmpty(dpVersionIds)) {
        return dpVersionIds.indexOf(dpVersionId) > -1;
      }
    }

    return false;
  }

  const runRp = () => {
    setProgressOn(true);
    setIsRun(false);

    if (selectItemHrchy !== null && !isEmpty(selectItemHrchy)) {
      version.targetItemHrchy = selectItemHrchy.map(item => item.hrchyCd);
      setDefaultValue(selectItemHrchy)
    }

    const updatedVersion = {
      ...version,
      planConfigObj: {
        ...version.planConfigObj,
        USE_SHIPMENT_TXN: useShipmentTxn,
        USE_MP_PRODUCTION_PLAN: useMpProductionPlan,
        PLAN_EXEC_DATE: dateUtils.formatDate(new Date(), DEFAULT_DATE_FORMAT)
      }
    };

    runRpService.runRp(updatedVersion)
      .then(response => {
        const { planVersion, count } = response.data;
        retrieveRpVersion(planVersion);

        if (count > 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_RpSuccess") });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoTargetRpPlan") });
        }
      })
      .catch(e => {
        const { status, data } = e?.response;
        if (status === 409) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: version.name }) });
        } else if (status === 400) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t(data.message) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_RpFailed") });
        }

        debug(e);
      })
      .finally(() => {
        reloadRpVersionFilter(reduxDispatch);
        setProgressOn(false);
      });
  }

  const saveChanges = () => {
    gridRef.current.api.stopEditing(false);

    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      runRpService.upsert(allStateData)
        .then(() => {
          retrieveRpVersion(version);
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { status } = e?.response;
          if (status === 409) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: version.name }) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }

          debug(e);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const createNewVersion = (createVersionParams) => {
    setProgressOn(true);
    runRpService.createVersion(createVersionParams)
      .then(response => {
        if (response.data?.message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(response.data.message.message) });
          return;
        }
        const { planVersion, count } = response.data;

        if (count > 0) {
          retrieveRpVersion(planVersion);
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_CreateVersion") });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_CreateVersionFailed") });
        }

      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_CreateVersionFailed") });
      })
      .then(() => {
        setProgressOn(false);
      });
  }

  const openRpFilterValue = useMemo(() => {
    const reduxKey = "RP_REVIEW";

    return reduxUtil.createObjectState(reduxKey, {
      // locationSearch: { originLocation: null, destLocation: locationFilter },
      filter: { category: filterValue.category, originLocation: null, destLocation: filterValue.locations },
      version,
      rpVersion: version,
      itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current,
      currentPage: 0,
      pageSize: 30
    });
  }, [itemSearch, version, filterValue])

  const openRpResult = (openRpFilterValue) => {
    const reduxKey = "RP_REVIEW";

    reduxDispatch(reduxUtil.updateViewState(openRpFilterValue));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }

  const handleCheckbox = e => {
    const value = e.target.checked;
    setOnlyRpApplied(value);

    const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, version, onlyRpApplied: value, currentPage, pageSize });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveChanges();
    } else if (action === "DELETE") {
      removeVersion();
    } else if (action === "RUN_WITHOUT_SAVING") {
      runRp();
    }
  }

  // 버전 기본값은 무조건 RP Settings 를 따라야 한다. 그런데 RP Settings 는 다른 탭에서 바뀌고,
  // 이 화면은 탭이 살아 있는 동안 onGridReady 때 읽은 스냅샷만 들고 있었다 → 설정을 바꿔도
  // "일부 옵션만 따라가는" 것처럼 보였다(스냅샷에 이미 있던 키만 맞음). 모달을 열기 직전에 다시 읽는다.
  const openModalWithLatestRpSettings = setModalOpen => {
    setProgressOn(true);
    retrievePeriodForAvgSales()
      .finally(() => {
        setProgressOn(false);
        setModalOpen(true);
      });
  }

  const handleModalOpen = (modalName) => {
    if (modalName === "CREATE") {
      openModalWithLatestRpSettings(setCreateNewModalOpen);
    } else if (modalName === "RUN") {
      if (hasDpVersion()) {
        if (isEmpty(DataState.getAllStateData(gridRef.current.api))) {
          setRunModalOpen(true);
        } else {
          if (!isNull(version.execTs)) {
            setDialogInfo({ open: true, actionName: "RUN_WITHOUT_SAVING", title: "warning", content: t("MSG_RpRunWithoutSave") });
          }
        }
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotExistConfiguredDpVersion") });
        openModalWithLatestRpSettings(setRpConfigModalOpen);
      }
    } else if (modalName === "CONFIG") {
      openModalWithLatestRpSettings(setRpConfigModalOpen);
    } else if (modalName === "MASS_UPDATE") {
      setMassUpdateModalOpen(true);
    }
  }

  const onGridReady = params => {
    retrieveRpVersion();
    retrieveDpVersion();
    retrievePeriodForAvgSales();
    retrieveItemHrchies();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveReplenishmentPlan();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading && version) {
      DataState.initialize(gridRef.current?.api);
      retrieveReplenishmentPlan();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading && version) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue?.locations, filterValue.category, version, onlyRpApplied, advancedFilters]);

  useEffect(() => {
    if (isRun) {
      runRp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRun])

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters])


  const iconColor = planVersion => {
    if (planVersion) {
      if (isEqual(planVersion.statusCd, RELEASED)) {
        return grey[900];
      }

      return isEmpty(planVersion.execTs) ? red[600] : green[600];
    }

    return null;
  }

  const isBlankVersion = isEmpty(version) || isNull(version);
  const isReleasedVersion = isEqual(version?.statusCd, RELEASED);

  useEffect(() => {
    if (version) {
      if (version?.statusCd === "PLANNED" && version?.execTs !== null) {
        setUseShipmentTxn(version?.planConfigObj?.USE_SHIPMENT_TXN ?? false);
        setUseMpProductionPlan(version?.planConfigObj?.USE_MP_PRODUCTION_PLAN ?? false);
      } else {
        setUseShipmentTxn(false);
        setUseMpProductionPlan(false);
      }
    }
  }, [version]);

  return (
    <>
      <FilterContainer>
        <VersionAutocomplete
          options={planVersions}
          value={version}
          onChange={(_, newValue) => setVersion(newValue)}
          label={t("version")}
          getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
          getIconColor={iconColor}
          getTooltip={RpUtil.getToolTip}
          inputIconSx={{ ml: 1 }}
        />
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs/>
        <RPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <FormControlLabel
          control={<Checkbox color="primary" name={"onlyRpApplied"} checked={onlyRpApplied} onChange={handleCheckbox} />}
          label={t("rpOnly")}
          key="ONLY_RP"
          sx={{ display: "inline-flex", minWidth: 110 }}
        />
        <Box id={"run-rp-filter-icon"}>
          {/* 계획 검토 화면이 권한 밖인 테넌트에서는 결과 버튼을 내보내지 않는다 */}
          {isPermitted("RP_REVIEW") && (
            <ResultButton disabled={isBlankVersion || isNull(version?.execTs)} onClick={() => openRpResult(openRpFilterValue)} />
          )}
          <ExcelExportButton title={title} service={runRpService} columnDefs={columnDefs.filter(column => column.headerName !== 'locCd')} params={getRequestParams(false)} isCsv translationColumns={["orderMethod", "orderCalendar", "itemType"]} />
          <CreateButton onClick={() => handleModalOpen("CREATE")} />
          <DeleteButton disabled={isBlankVersion || isReleasedVersion} onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_VersionPermanentlyDelete", { version: version?.name }) })} />
          <SaveButton disabled={isBlankVersion || isReleasedVersion}
            onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <BulkValueUpdateButton disabled={isBlankVersion || isReleasedVersion} onClick={() => handleModalOpen("MASS_UPDATE")} />
          <RunButton label="runReplenishPlan" disabled={isBlankVersion || isReleasedVersion} onClick={() => handleModalOpen("RUN")} />
          <SettingsButton label="settings" disabled={isBlankVersion} onClick={() => handleModalOpen("CONFIG")} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="run-rp-grid" className="ag-theme-balham grid-area" sx={{ position: 'relative', height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column', mb: 2 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <AgGridReact
              ref={gridRef}
              rowData={rpPolicies}
              {...defaultGridMemo}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <>
              <ResizableFilterPanel>
                <AdvancedFilter
                  columnDefs={columnDefs}
                  advancedFilters={advancedFilters}
                  onApply={setAdvancedFilters}
                  onClose={() => setOpenFilter(false)}
                />
              </ResizableFilterPanel>
            </>
          )}
        </Box>
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <CreateVersionModal
        viewName={viewName}
        width={500}
        moduleCd={RP}
        modalOpen={createNewModalOpen}
        setModalOpen={setCreateNewModalOpen}
        defaultValue={rpSettingValue.current}
        planVersions={planVersions}
        createVersion={createNewVersion}
        dpVersions={dpVersions}
        iconColor={iconColor}
        setSnackInfo={setSnackInfo}
      />
      <RunModal
        title={"runReplenishPlan"}
        viewName={viewName}
        modalOpen={runModalOpen}
        setModalOpen={setRunModalOpen}
        defaultValue={defaultValue}
        setSelectedItemHrchy={setSelectItemHrchy}
        selectedPlanVersion={version}
        setIsRun={setIsRun}
        useShipmentTxn={useShipmentTxn}
        onUseShipmentTxnChange={setUseShipmentTxn}
        useMpProductionPlan={useMpProductionPlan}
        onUseMpProductionPlan={setUseMpProductionPlan}
      />
      <VersionSettingsModal
        viewName={viewName}
        moduleCd={RP}
        modalOpen={rpConfigModalOpen}
        setModalOpen={setRpConfigModalOpen}
        defaultValue={rpSettingValue.current}
        selectedPlanVersion={version}
        dpVersions={dpVersions}
        retrievePlanVersion={retrieveRpVersion}
        setSnackInfo={setSnackInfo}
        setProgressOn={setProgressOn}
      />
      <RpBulkValueUpdateModal
        addedTargetColumnDefs={addedBulkUpdateTargets}
        updateTargets={updateTargets}
        service={runRpService}
        modalOpen={massUpdateModalOpen}
        setModalOpen={setMassUpdateModalOpen}
        selectedPlanVersion={version}
        retrieve={retrieveRpVersion}
        setSnackInfo={setSnackInfo}
        setProgressOn={setProgressOn}
        moduleCd={RP}
      />
    </>
  );
};

export default withTranslation()(RunRp);
